'use client';

import React from 'react';

import {
  HorizontalPositions,
  Sizes,
  cx,
  ButtonVariant,
  Color,
  HorizontalPosition,
  Size,
} from '@UI/lib/utils';
import LoadingSpinner from '@/components/UI/assets/LoadingSpinner';

import {
  getButtonColors,
  getButtonProportions,
  iconSizes,
} from './button.styles';

export interface ButtonIconOrSpinnerProps {
  loading: boolean;
  iconSize: string;
  iconPosition: string;
  Icon: React.ElementType | undefined;
  needMargin: boolean;
  classNames?: string;
}

export const ButtonIconOrSpinner = ({
  loading,
  iconSize,
  iconPosition,
  Icon,
  needMargin,
  classNames = '',
}: ButtonIconOrSpinnerProps) => {
  Icon = Icon!;

  const margin = !needMargin
    ? ''
    : iconPosition === HorizontalPositions.Left
      ? cx('-ml-1', 'mr-1.5')
      : cx('-mr-1', 'ml-1.5');

  const defaultSpinnerSize = cx(iconSize);
  const spinnerSize: { [key: string]: string } = {
    default: defaultSpinnerSize,
    entering: defaultSpinnerSize,
    entered: iconSize,
    exiting: iconSize,
    exited: defaultSpinnerSize,
  };

  return loading ? (
    <LoadingSpinner
      className={cx(
        'animate-spin shrink-0',
        margin,
        spinnerSize.default,
        classNames,
      )}
      style={{ transition: `width 150ms` }}
    />
  ) : (
    <Icon className={cx('shrink-0', iconSize, margin, classNames)} />
  );
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  as?: React.ElementType;
  icon?: React.ElementType;
  iconPosition?: HorizontalPosition;
  size?: Size;
  color?: Color;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  href?: string;
  title?: string;
  target?: string;
  expanded?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, forwardedRef) => {
    const {
      as: Component = 'button',
      icon,
      iconPosition = HorizontalPositions.Left,
      size = Sizes.SM,
      color,
      variant = 'primary',
      disabled,
      loading = false,
      children,
      className,
      expanded = false,
      ...other
    } = props;

    const Icon = icon;

    const isDisabled = loading || disabled;
    const showButtonIconOrSpinner = Icon !== undefined || loading;
    const needIconMargin = children ? true : false;

    const iconSize = cx(iconSizes[size].height, iconSizes[size].width);

    const buttonShapeStyles =
      variant !== 'ghost'
        ? cx(
            // common
            'rounded-revving-default border',
            // light
            'shadow-revving-input',
            // dark
            'dark:shadow-darkRevving-input',
          )
        : '';

    const buttonColorStyles = getButtonColors(variant, color);
    const buttonProportionStyles = getButtonProportions(variant)[size];

    return (
      <Component
        ref={forwardedRef}
        className={cx(
          // common
          'shrink-0 inline-flex justify-center items-center group font-medium outline-none',
          buttonShapeStyles,
          buttonProportionStyles.paddingX,
          buttonProportionStyles.paddingY,
          buttonProportionStyles.fontSize,
          // buttonColorStyles.textColor,
          buttonColorStyles.bgColor,
          buttonColorStyles.borderColor,
          buttonColorStyles.hoverBorderColor,
          !isDisabled
            ? cx(
                getButtonColors(variant, color).hoverTextColor,
                getButtonColors(variant, color).hoverBgColor,
                getButtonColors(variant, color).hoverBorderColor,
              )
            : 'opacity-50 cursor-not-allowed',
          className,
        )}
        disabled={isDisabled}
        {...other}
      >
        {showButtonIconOrSpinner &&
        iconPosition !== HorizontalPositions.Right ? (
          <ButtonIconOrSpinner
            loading={loading}
            iconSize={iconSize}
            iconPosition={iconPosition}
            Icon={Icon}
            needMargin={needIconMargin}
            classNames={buttonColorStyles.textColor}
          />
        ) : null}

        {children ? (
          <span
            className={cx(
              expanded ? 'w-full' : '',
              buttonColorStyles.textColor,
              'whitespace-nowrap font-accent',
            )}
          >
            {children}
          </span>
        ) : null}

        {showButtonIconOrSpinner &&
        iconPosition === HorizontalPositions.Right ? (
          <ButtonIconOrSpinner
            loading={loading}
            iconSize={iconSize}
            iconPosition={iconPosition}
            Icon={Icon}
            needMargin={needIconMargin}
            classNames={buttonColorStyles.textColor}
          />
        ) : null}
      </Component>
    );
  },
);

Button.displayName = 'Button';

export { Button, type ButtonProps };
